import { string as zString } from 'zod';
import { ga, pushDataLayer } from '@hungryroot/google-analytics';
import {
  StatusCode,
  postAppleLogin,
  type SignupResponse,
} from '@hungryroot/rest';
import { funnelAnalytics } from '@hungryroot/segment';
import { logSso } from './o11y';
import { loadScript } from './provider';

export type AppleSdk = AppleSignInAPI.AppleID;

export const SCRIPT_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

const APPLE_CLIENT_ID = zString().parse(process.env.PUBLIC_APPLE_CLIENT_ID);
const APPLE_REDIRECT_URI = zString().parse(
  process.env.PUBLIC_APPLE_REDIRECT_URI
);

type Params = {
  onReady: (sdk: AppleSignInAPI.AppleID) => void;
};

const APPLE_INIT_DATA = {
  clientId: APPLE_CLIENT_ID,
  redirectURI: APPLE_REDIRECT_URI,
  state: '{{ APPLE_STATE }}',
  scope: 'name email',
  usePopup: true,
};

export async function loadAppleSdk({ onReady }: Params) {
  if (window.AppleID !== undefined) {
    window.AppleID.auth.init(APPLE_INIT_DATA);

    onReady(window.AppleID);
    return;
  }

  const sdk = await loadScript({ src: SCRIPT_SRC, globalProperty: 'AppleID' });

  sdk.auth.init(APPLE_INIT_DATA);

  onReady(sdk);
}

type AuthParams = {
  appleId: {
    auth: Pick<AppleSignInAPI.AppleID['auth'], 'signIn'>;
  };
  onLoginSuccess: (data: SignupResponse) => void;
  onSignupSuccess: (data: SignupResponse) => void;
  onError: (e: unknown) => void;
  /** Marks a freemium (guest-flow) signup if this auth creates an account. */
  isGuestFlow?: boolean;
};

export function appleAuth({
  onError,
  onLoginSuccess,
  appleId,
  onSignupSuccess,
  isGuestFlow,
}: AuthParams) {
  const segment = funnelAnalytics();

  logSso({
    lifecycle: 'initiated',
    provider: 'apple',
  });

  appleId.auth
    .signIn()
    .then(async ({ authorization: { code, id_token, state } }) => {
      const resp = await postAppleLogin({
        code,
        idToken: id_token,
        isWeb: true,
        state,
        isGuestFlow,
      });

      if (resp.status !== StatusCode.SuccessOK) {
        throw {
          message: 'api_apple_login_failed',
          statusCode: resp.status,
        };
      }

      const {
        data: { created, customer },
      } = resp;
      const { id: customerId, email: customerEmail } = customer;
      const eventType = created === true ? 'signup' : 'login';
      logSso({
        lifecycle: 'success',
        provider: 'apple',
        eventType,
      });

      pushDataLayer({
        event: `hr.${eventType}.success`,
        [eventType]: {
          method: 'apple',
        },
        customer: {
          id: customerId,
          email: customerEmail,
        },
      });

      if (created === true) {
        segment.completedRegistration(customer, 'apple');
        onSignupSuccess(customer);
        return;
      }
      onLoginSuccess(customer);
    })
    .catch((e) => {
      logSso({
        lifecycle: 'error',
        provider: 'apple',
        error: e,
      });
      ga('send', 'event', 'login', 'failure');
      onError(e);
    });
}
